import * as React from "react";
import { PropsWithChildren, ReactElement, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sidebar, SidebarContent } from "src/components/ui/sidebar";
import { SurveyStatus } from "src/redux/survey/survey.types";
import { SurveyResultsSidebarDetails } from "./components/results-sidebar-details";
import { SurveyResultsSidebarHeader } from "./components/results-sidebar-header";
import SurveyResultsSidebarQuestionList from "./components/results-sidebar-question-list";
import { useViewSurveyChildren, ViewSurveyChildrenProps } from "./useViewSurveyChildren";

type SurveyResultsSidebarProps = React.ComponentProps<typeof Sidebar> & ViewSurveyChildrenProps & {
  createSummary: () => void;
  createMediaPlan: () => void;
};

const Layout = ({ children, ...props }: PropsWithChildren) => {
  return (
    <Sidebar
      {...props}
      variant="floating"
      size="survey-results"
    >
      {children}
    </Sidebar>
  );
};

export const SurveyResultsSidebar = ({ surveyId, isShare, createSummary, createMediaPlan }: SurveyResultsSidebarProps): ReactElement => {
  const { survey, stats, summary } = useViewSurveyChildren(surveyId, isShare);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  if (!survey || !stats) {
    return (
      <Layout>
        <SurveyResultsSidebarHeader
          surveyId={surveyId}
          isShare={isShare}
          createSummary={createSummary}
          createMediaPlan={createMediaPlan}
        />
        <SurveyResultsSidebarDetails
          surveyId={surveyId}
          isShare={isShare}
        />
        <SurveyResultsSidebarQuestionList variant="skeleton" />
      </Layout>
    );
  }

  const renderFadeMask = () => {
    if (scrollRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = scrollRef.current;
      const newIsAtBottom = scrollHeight < clientHeight + scrollTop + 1;

      newIsAtBottom !== isAtBottom && setIsAtBottom(newIsAtBottom);
    }
  };

  const onClickQuestion = (index: string): void => {
    // Do not change String(Number()). It's needed for js concatination shenanigans
    searchParams.set("question", index === "summary" ? 'summary' : String(Number(index) + 1));
    setSearchParams(searchParams);
  };

  // This handles atBottom checks when "SurveyDetails" gets expanded/collapsed
  scrollRef.current && renderFadeMask();

  const isComplete = survey.surveyStatus === SurveyStatus.Complete;
  const activeQuestion = searchParams.get('question') ?? (isComplete ? 'summary' : '0');

  return (
    <Layout>
      <SurveyResultsSidebarHeader
        surveyId={surveyId}
        isShare={isShare}
        createSummary={createSummary}
        createMediaPlan={createMediaPlan}
      />
      <SidebarContent
        className={`gap-0 ${!isAtBottom ? "survey-sidebar" : ""}`}
        onScroll={renderFadeMask}
        ref={scrollRef}
      >
        <SurveyResultsSidebarDetails
          surveyId={surveyId}
          isShare={isShare}
        />
        <SurveyResultsSidebarQuestionList
          variant="data"
          activeQuestion={activeQuestion}
          questions={survey.questions}
          questionStats={stats.questions}
          handleClick={onClickQuestion}
          showSummaries={Boolean(summary?.summaries.length)}
          surveyStatus={survey.surveyStatus}
        />
        {/* only on desktop, if we want shrinking sidebar, rounding doesn't work properly */}
        {/* <div className="flex grow bg-gray-101 rounded-b-lg"></div> */}
      </SidebarContent>
    </Layout>
  );
};
